import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { components } from '@trackercop/contracts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { CookieCategoryChip } from '@/components/category-chip';
import { ReviewPopover } from '@/components/review-popover';
import { COOKIE_CATEGORIES } from '@/lib/constants';
import { Pagination } from './scripts-tab';

type ConsentMode = components['schemas']['ConsentMode'];
type ReviewMap = Map<string, components['schemas']['FindingReview']>;

interface CookiesTabProps {
  crawlId: string;
  consentMode: ConsentMode | 'all';
  gtmContainerId: string | 'all';
  reviews: ReviewMap;
}

export function CookiesTab({ crawlId, consentMode, gtmContainerId, reviews }: CookiesTabProps) {
  const [domain, setDomain] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const filters = useMemo(
    () => ({
      page,
      pageSize,
      domain: domain || undefined,
      category: (category || undefined) as components['schemas']['CookieCategory'] | undefined,
      consentMode: consentMode === 'all' ? undefined : consentMode,
      gtmContainerId: gtmContainerId === 'all' ? undefined : gtmContainerId,
    }),
    [page, domain, category, consentMode, gtmContainerId],
  );

  const query = useQuery({
    queryKey: queryKeys.crawls.cookies(crawlId, filters),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/crawls/{crawlId}/cookies', {
        params: { path: { crawlId }, query: filters },
      });
      if (error) throw error;
      return data;
    },
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 rounded-md border bg-background p-2">
        <Input
          placeholder="Domain filter…"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          className="h-8 w-60"
          data-testid="cookie-domain-filter"
        />
        <Select value={category} onChange={(e) => setCategory(e.target.value)} className="h-8 w-48">
          <option value="">Any category</option>
          {COOKIE_CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </Select>
      </div>

      <Table>
        <THead>
          <TR>
            <TH>Name</TH>
            <TH>Domain</TH>
            <TH>Category</TH>
            <TH>GTM</TH>
            <TH>Vendor</TH>
            <TH>Pages</TH>
            <TH>Consent</TH>
            <TH>Flags</TH>
            <TH>Review</TH>
          </TR>
        </THead>
        <TBody>
          {query.isLoading ? (
            <TR><TD colSpan={9} className="text-center text-xs text-muted-foreground">Loading…</TD></TR>
          ) : items.length === 0 ? (
            <TR><TD colSpan={9} className="text-center text-xs text-muted-foreground">No cookies matched.</TD></TR>
          ) : (
            items.map((c) => {
              const review = reviews.get(`cookie:${c.id}`);
              return (
                <TR key={c.id}>
                  <TD className="font-mono text-xs">{c.name}</TD>
                  <TD className="font-mono text-xs">{c.domain}</TD>
                  <TD><CookieCategoryChip category={c.category} /></TD>
                  <TD>{c.gtmContainerId ? <Badge variant="info">GTM</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TD>
                  <TD className="text-xs">{c.vendor ?? '—'}</TD>
                  <TD>
                    <Badge variant="muted" title={c.pageIds?.join('\n')}>
                      {c.pageCount ?? 1}
                    </Badge>
                  </TD>
                  <TD>
                    {c.setBeforeConsent ? <Badge variant="destructive">Before</Badge> : <Badge variant="muted">After</Badge>}
                  </TD>
                  <TD className="text-xs text-muted-foreground">
                    {c.httpOnly && 'HttpOnly '}
                    {c.secure && 'Secure '}
                    {c.sameSite}
                  </TD>
                  <TD>
                    <ReviewPopover
                      crawlId={crawlId}
                      entityType="cookie"
                      entityId={c.id}
                      currentStatus={review?.status ?? 'unreviewed'}
                      currentNote={review?.note ?? null}
                    />
                  </TD>
                </TR>
              );
            })
          )}
        </TBody>
      </Table>
      <Pagination page={page} pageCount={pageCount} total={total} onChange={setPage} />
    </div>
  );
}
